// ===================================
// KAIXO - Minijuego de Reacción (Reto Global)
// ===================================
// Señal visual aparece tras delay aleatorio 1-3s. El jugador debe pulsar
// EXACTO al target del nivel actual (1000ms, 3000ms, ...). Si clava ±tolerancia,
// desbloquea el siguiente nivel para todo el mundo.
// ===================================

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import { reactionGetState, reactionGetLeaderboard, reactionGetLeaderboardHistory, reactionPlay } from '../../services/api';
import './ReactionGame.css';

const PHASE = {
    IDLE: 'idle',       // Pantalla inicial con info
    WAITING: 'waiting', // Esperando señal (countdown + pausa aleatoria)
    ACTIVE: 'active',   // Señal visible, midiendo
    RESULT: 'result',   // Mostrando resultado del intento
    LOCKED: 'locked'    // Sin intentos hoy
};

function formatMs(ms) {
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(3)} s`;
}

export default function ReactionGame({ onClose }) {
    const [phase, setPhase] = useState(PHASE.IDLE);
    const [state, setState] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardHistory, setLeaderboardHistory] = useState([]);
    const [boardTab, setBoardTab] = useState('today'); // 'today' | 'history'
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const signalShownAtRef = useRef(0);
    const waitTimerRef = useRef(null);

    // Cargar estado y leaderboard al abrir
    useEffect(() => {
        loadState();
        loadLeaderboard();
        return () => {
            if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
        };
    }, []);

    const loadState = async () => {
        try {
            const { data } = await reactionGetState();
            setState(data);
            if (data.attemptsUsedToday >= data.attemptsMax) {
                setPhase(PHASE.LOCKED);
            }
        } catch (e) {
            setError('No se pudo cargar el estado del juego');
        }
    };

    const loadLeaderboard = async () => {
        try {
            const [today, history] = await Promise.all([
                reactionGetLeaderboard(),
                reactionGetLeaderboardHistory()
            ]);
            setLeaderboard(today.data?.leaderboard || []);
            setLeaderboardHistory(history.data?.leaderboard || []);
        } catch {}
    };

    // Arranca: countdown 3-2-1 y luego delay aleatorio hasta mostrar la señal.
    const startGame = () => {
        if (!state || phase !== PHASE.IDLE) return;
        setResult(null);
        setPhase(PHASE.WAITING);
        // Countdown visual simplificado: tras 2.5s arranca delay aleatorio
        // (entre 500-2000ms adicionales para impedir bots con timer fijo).
        waitTimerRef.current = setTimeout(() => {
            const extraDelay = 500 + Math.random() * 1500;
            waitTimerRef.current = setTimeout(() => {
                signalShownAtRef.current = performance.now();
                setPhase(PHASE.ACTIVE);
            }, extraDelay);
        }, 2500);
    };

    // El jugador pulsa — calculamos delta y enviamos al servidor.
    const onTap = async () => {
        if (phase !== PHASE.ACTIVE) {
            // Tap antes de tiempo: resultado "anticipado", no cuenta como intento.
            if (phase === PHASE.WAITING) {
                if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
                setPhase(PHASE.IDLE);
                setError('Pulsaste antes de la señal. No cuenta como intento.');
                setTimeout(() => setError(''), 2500);
            }
            return;
        }
        const tapAt = performance.now();
        const elapsed = tapAt - signalShownAtRef.current;
        const targetMs = state.currentLevelMs;
        const deltaMs = Math.abs(elapsed - targetMs);

        setPhase(PHASE.RESULT);
        try {
            const { data } = await reactionPlay(deltaMs);
            setResult({
                deltaMs: data.attempt.deltaMs,
                won: data.attempt.won,
                levelMs: data.attempt.levelMs,
                attemptsUsedToday: data.attemptsUsedToday,
                attemptsMax: data.attemptsMax,
                newLevelMs: data.currentLevelMs
            });
            // Si ganó, refrescar estado global + leaderboard
            if (data.attempt.won) {
                setTimeout(() => { loadState(); loadLeaderboard(); }, 500);
            } else {
                setState(prev => ({ ...prev, attemptsUsedToday: data.attemptsUsedToday }));
                loadLeaderboard();
            }
        } catch (e) {
            setError(e.response?.data?.error || e.message);
            setResult({ error: true });
        }
    };

    const playAgain = () => {
        if (!state) return;
        if ((result?.attemptsUsedToday || 0) >= (state.attemptsMax || 3)) {
            setPhase(PHASE.LOCKED);
            return;
        }
        setPhase(PHASE.IDLE);
        setResult(null);
    };

    return ReactDOM.createPortal(
        <div className="rg-overlay" onClick={(e) => e.target.classList.contains('rg-overlay') && onClose()}>
            <div className="rg-container">
                <div className="rg-header">
                    <h2 className="rg-title">
                        <Icons.Pulse size={22} /> Reto global
                    </h2>
                    <button className="rg-close" onClick={onClose} aria-label="Cerrar">
                        <Icons.Close size={20} />
                    </button>
                </div>

                {!state ? (
                    <div className="rg-loading">Cargando…</div>
                ) : (
                    <>
                        <div className="rg-level-info">
                            <div className="rg-level-label">Nivel actual</div>
                            <div className="rg-level-value">{formatMs(state.currentLevelMs)}</div>
                            <div className="rg-level-sub">
                                Pulsa justo al cumplirse ese tiempo desde que aparezca la señal.
                                Margen de acierto: <strong>±{state.toleranceMs} ms</strong>.
                            </div>
                        </div>

                        <div
                            className={`rg-board rg-phase-${phase}`}
                            onClick={onTap}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') onTap(); }}
                        >
                            {phase === PHASE.IDLE && (
                                <div className="rg-board-text">
                                    <div className="rg-board-hint">Toca para empezar</div>
                                    <div className="rg-board-quota">
                                        {state.attemptsUsedToday}/{state.attemptsMax} intentos hoy
                                    </div>
                                    <button
                                        className="rg-start-btn"
                                        onClick={(e) => { e.stopPropagation(); startGame(); }}
                                    >
                                        Empezar
                                    </button>
                                </div>
                            )}
                            {phase === PHASE.WAITING && (
                                <div className="rg-board-text">
                                    <div className="rg-board-hint">Prepárate…</div>
                                    <div className="rg-board-sub">Cuando veas la señal, cuenta en tu cabeza</div>
                                </div>
                            )}
                            {phase === PHASE.ACTIVE && (
                                <div className="rg-board-signal">
                                    <div className="rg-board-pulse" />
                                    <div className="rg-board-hint">¡Ya! Cuenta y pulsa</div>
                                </div>
                            )}
                            {phase === PHASE.RESULT && result && (
                                <div className="rg-board-text">
                                    {result.error ? (
                                        <div className="rg-board-hint rg-error">Error al enviar</div>
                                    ) : result.won ? (
                                        <>
                                            <div className="rg-board-hint rg-win">🏆 ¡Exacto!</div>
                                            <div className="rg-board-sub">
                                                Diferencia: <strong>{result.deltaMs.toFixed(2)} ms</strong>
                                            </div>
                                            <div className="rg-board-sub rg-win-unlock">
                                                Nuevo nivel mundial: {formatMs(result.newLevelMs)}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="rg-board-hint">
                                                {result.deltaMs < 50 ? '¡Muy cerca!' : result.deltaMs < 200 ? 'Cerca' : 'Lejos'}
                                            </div>
                                            <div className="rg-board-sub">
                                                Diferencia: <strong>{result.deltaMs.toFixed(2)} ms</strong>
                                            </div>
                                            <div className="rg-board-sub">
                                                {result.attemptsUsedToday}/{result.attemptsMax} intentos usados hoy
                                            </div>
                                        </>
                                    )}
                                    <button
                                        className="rg-start-btn"
                                        onClick={(e) => { e.stopPropagation(); playAgain(); }}
                                    >
                                        {(result.attemptsUsedToday >= result.attemptsMax) ? 'Cerrar' : 'Otro intento'}
                                    </button>
                                </div>
                            )}
                            {phase === PHASE.LOCKED && (
                                <div className="rg-board-text">
                                    <div className="rg-board-hint">Ya usaste tus intentos hoy</div>
                                    <div className="rg-board-sub">Vuelve mañana. El reto global sigue activo.</div>
                                </div>
                            )}
                        </div>

                        {error && <div className="rg-error-bar">{error}</div>}

                        <div className="rg-sections">
                            <div className="rg-section">
                                <div className="rg-board-tabs">
                                    <button
                                        className={`rg-board-tab ${boardTab === 'today' ? 'is-active' : ''}`}
                                        onClick={() => setBoardTab('today')}
                                    >Top de hoy</button>
                                    <button
                                        className={`rg-board-tab ${boardTab === 'history' ? 'is-active' : ''}`}
                                        onClick={() => setBoardTab('history')}
                                    >Mundial histórico</button>
                                </div>
                                {(() => {
                                    const list = boardTab === 'today' ? leaderboard : leaderboardHistory;
                                    if (list.length === 0) {
                                        return (
                                            <div className="rg-empty">
                                                {boardTab === 'today'
                                                    ? 'Aún nadie ha jugado hoy.'
                                                    : 'Aún no hay registros históricos en este nivel.'}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="rg-list">
                                            {list.slice(0, 10).map(entry => (
                                                <div key={`${boardTab}-${entry.userId}-${entry.rank}`} className="rg-list-row">
                                                    <span className="rg-rank">#{entry.rank}</span>
                                                    <span className="rg-name">{entry.name}</span>
                                                    {entry.country && <span className="rg-country">{entry.country}</span>}
                                                    <span className={`rg-delta ${entry.won ? 'rg-delta-win' : ''}`}>
                                                        {entry.deltaMs.toFixed(2)} ms
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            {state.winners?.length > 0 && (
                                <div className="rg-section">
                                    <div className="rg-section-title">Desbloqueos históricos</div>
                                    <div className="rg-list">
                                        {state.winners.slice(0, 8).map((w, i) => (
                                            <div key={i} className="rg-list-row">
                                                <span className="rg-rank">⚡</span>
                                                <span className="rg-name">{w.name}</span>
                                                <span className="rg-delta">{formatMs(w.levelMs)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}
